/**
 * Onthouden categories and filters (spec §8.2, contract §8).
 * Category labels are canonical Dutch copy — keep exact.
 */
import type { Note } from '@/types'

export const NOTE_CATEGORIES = [
  'Algemeen',
  'Persoonlijk',
  'Werk',
  'Gezondheid',
  'Boodschappen',
  'Ideeën',
] as const

export type NoteCategory = (typeof NOTE_CATEGORIES)[number]

export const DEFAULT_NOTE_CATEGORY: NoteCategory = 'Algemeen'

export function isNoteCategory(value: string): value is NoteCategory {
  return (NOTE_CATEGORIES as readonly string[]).includes(value)
}

export const NOTE_FILTERS = [
  { value: 'all', label: 'Alles' },
  { value: 'pinned', label: 'Vastgezet' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'done', label: 'Gedaan' },
  { value: 'archived', label: 'Gearchiveerd' },
] as const

export type NoteFilterValue = (typeof NOTE_FILTERS)[number]['value']

/** Applies a filter chip. 'Alles' hides archived; 'Gearchiveerd' shows only archived. */
export function filterNotes(notes: Note[], filter: NoteFilterValue): Note[] {
  switch (filter) {
    case 'archived':
      return notes.filter((note) => note.archived)
    case 'pinned':
      return notes.filter((note) => note.pinned && !note.archived)
    case 'urgent':
      return notes.filter((note) => note.urgent && !note.archived)
    case 'done':
      return notes.filter((note) => note.done && !note.archived)
    case 'all':
      return notes.filter((note) => !note.archived)
  }
}

/** Canonical order: pinned first, then updatedAt descending. */
export function sortNotes(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    return b.updatedAt.localeCompare(a.updatedAt)
  })
}

/** Case-insensitive search across title, content and category. */
export function searchNotes(notes: Note[], query: string): Note[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return notes
  return notes.filter((note) =>
    [note.title, note.content, note.category].some((field) => field.toLowerCase().includes(needle)),
  )
}

/** Full view pipeline: filter -> search -> sort. */
export function visibleNotes(notes: Note[], filter: NoteFilterValue, query: string): Note[] {
  return sortNotes(searchNotes(filterNotes(notes, filter), query))
}
